import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, History, Loader2, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FINAL = ['observed', 'unavailable'];

const Cell = ({ obs }) => {
  if (!obs) return <span className="text-slate-400" data-testid="outcome-pending">pending</span>;
  if (obs.status !== 'observed') return <span className="text-slate-400 italic" title={obs.reason} data-testid="outcome-unavailable">unavailable</span>;
  const up = obs.change_pct >= 0;
  return (
    <span className={`font-mono ${up ? 'text-emerald-700' : 'text-rose-700'}`} title={`close $${obs.close} on ${obs.observed_at}`} data-testid="outcome-observed">
      {up ? '+' : ''}{obs.change_pct.toFixed(2)}%
    </span>
  );
};

const StatBox = ({ cp }) => (
  <div className="rounded-lg border border-gray-200 bg-white/70 p-3" data-testid={`outcome-stats-${cp.checkpoint_trading_days}d`}>
    <p className="text-xs font-semibold text-gray-700 mb-1">+{cp.checkpoint_trading_days} trading day{cp.checkpoint_trading_days > 1 ? 's' : ''}</p>
    {cp.available ? (
      <>
        <p className="text-sm"><span className="font-mono font-semibold">{cp.win_rate_pct}%</span> closed above reference <span className="text-gray-500">({cp.wins} up / {cp.losses} down or flat)</span></p>
        <p className="text-xs text-gray-600">median {cp.median_change_pct >= 0 ? '+' : ''}{cp.median_change_pct}% · mean {cp.mean_change_pct >= 0 ? '+' : ''}{cp.mean_change_pct}% · range {cp.worst_change_pct}% to +{cp.best_change_pct}%</p>
      </>
    ) : (
      <p className="text-sm text-gray-600 italic" data-testid="outcome-stats-unavailable">{cp.message}</p>
    )}
    <p className="text-[11px] text-gray-500 mt-1">{cp.evaluated} evaluated · {cp.pending} pending · {cp.unavailable} unavailable · {cp.excluded_invalid} excluded</p>
  </div>
);

const PickOutcomes = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/recommendations/outcomes?limit=120`),
      axios.get(`${API}/recommendations/outcomes/stats`).catch(() => null),
    ])
      .then(([r, s]) => { setData(r.data); setStats(s?.data || null); })
      .catch(() => setError('Outcome records are unavailable right now.'))
      .finally(() => setLoading(false));
  }, [open, data]);

  const lastEval = data?.last_evaluation?.finished_at ? new Date(data.last_evaluation.finished_at).toLocaleString() : null;

  return (
    <Card className="glass-effect border-0 shadow-lg" data-testid="pick-outcomes-card">
      <CardHeader className="pb-2">
        <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-left" data-testid="pick-outcomes-toggle">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2"><History className="h-5 w-5 text-indigo-600" /> Pick History &amp; Outcomes</CardTitle>
          {open ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
        </button>
        <p className="text-xs text-gray-500 mt-1">Every published pick, with the actual delayed daily closes recorded afterwards by a scheduled daily job. Stock-price change only — <strong>not</strong> options-strategy profit or loss. Nothing is estimated.</p>
      </CardHeader>
      {open && (
        <CardContent>
          {loading && <div className="flex items-center gap-2 text-sm text-gray-500 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading records…</div>}
          {error && <p className="text-sm text-red-600" data-testid="pick-outcomes-error">{error}</p>}
          {data && (
            <>
              {stats && (
                <div className="mb-4" data-testid="pick-outcomes-stats">
                  <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5 mb-2"><BarChart3 className="h-4 w-4 text-indigo-600" /> Descriptive statistics (stock-price movement only)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {Object.values(stats.checkpoints).map((cp) => <StatBox key={cp.checkpoint_trading_days} cp={cp} />)}
                  </div>
                  <div className="mt-2 text-[11px] text-gray-500 space-y-0.5" data-testid="pick-outcomes-stats-methodology">
                    {Object.values(stats.methodology).map((m, i) => <p key={i}>• {m}</p>)}
                    {stats.invalid_reference_records > 0 && <p>• {stats.invalid_reference_records} record{stats.invalid_reference_records === 1 ? '' : 's'} had no valid reference price at publication and are excluded from every statistic.</p>}
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-500 mb-3" data-testid="pick-outcomes-summary">
                {data.total_records} picks recorded · {data.summary.observed_checkpoints} observed checkpoints (shown rows) · {data.summary.pending_or_unavailable} pending/unavailable · source: {data.price_source.replace(/_/g, ' ')}{lastEval ? ` · last job ${data.last_evaluation.status.replace(/_/g, ' ')} ${lastEval}` : ''}
              </p>
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full text-xs sm:text-sm" data-testid="pick-outcomes-table">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-3">Published</th><th className="py-2 pr-3">Symbol</th><th className="py-2 pr-3 hidden sm:table-cell">Suggested strategy</th><th className="py-2 pr-3">Ref. close</th>
                      <th className="py-2 pr-3">+1d</th><th className="py-2 pr-3">+5d</th><th className="py-2 pr-3">+20d</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.records.map((r) => {
                      const by = Object.fromEntries(r.observations.filter((o) => FINAL.includes(o.status)).map((o) => [o.checkpoint_trading_days, o]));
                      const badRef = r.status === 'invalid_reference';
                      return (
                        <tr key={`${r.pick_date}-${r.model}-${r.symbol}`} className="border-b border-gray-100" data-testid="pick-outcome-row">
                          <td className="py-1.5 pr-3 font-mono text-gray-700">{r.pick_date}</td>
                          <td className="py-1.5 pr-3 font-semibold">{r.symbol}</td>
                          <td className="py-1.5 pr-3 text-gray-600 hidden sm:table-cell">{r.recommended_strategy || '—'}</td>
                          <td className="py-1.5 pr-3 font-mono">{badRef ? <span className="text-slate-400 italic" title={r.invalid_reason}>no valid price</span> : `$${r.reference_price.toFixed(2)}`}</td>
                          {badRef ? <td className="py-1.5 pr-3 text-slate-400 italic" colSpan={3}>not evaluated</td> : (
                            <><td className="py-1.5 pr-3"><Cell obs={by[1]} /></td><td className="py-1.5 pr-3"><Cell obs={by[5]} /></td><td className="py-1.5 pr-3"><Cell obs={by[20]} /></td></>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 text-[11px] text-gray-500 space-y-1" data-testid="pick-outcomes-methodology">
                {Object.values(data.methodology).map((m, i) => <p key={i}>• {m}</p>)}
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default PickOutcomes;
